import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CustomerAttendanceModal } from '../attendance/CustomerAttendanceModal';
import { DetailedAttendanceModal } from '../attendance/DetailedAttendanceModal';
import { IconLogin, IconLogout, IconClock } from '@tabler/icons-react';
import { unifiedNFCService } from '../../../services/nfc/unifiedNFCService';
import { showNotification } from '@mantine/notifications';
import { apiClient } from '@/services/api';
import {
  getNfcNotificationMode,
  canSendBrowserNotification,
} from '@/shared/hooks/useNfcNotificationMode';

const NFC_DISABLED_PATHS = ['/entry-exit/attendance-monitor'];

interface CustomerInfo {
  id: number;
  name: string;
  name_kana: string;
  birth_date?: string;
  gender?: 'male' | 'female' | 'other';
  photo_url?: string;
  avatar?: string;
  memo?: string;
  store: {
    id: number;
    name: string;
  };

  status: 'active' | 'paused' | 'withdrawn';
  // 入退室情報
  action?: 'check_in' | 'check_out';
  check_in_time?: string;
  check_out_time?: string;
  stay_duration?: string;
}

export const CustomerNFCHandler: React.FC = () => {
  const location = useLocation();
  const isDisabled = NFC_DISABLED_PATHS.some((p) => location.pathname.startsWith(p));

  const [showModal, setShowModal] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerInfo | null>(null);
  const [attendanceData, setAttendanceData] = useState<
    | {
        action: 'check_in' | 'check_out';
        check_in_time?: string;
        check_out_time?: string;
        stay_duration?: string;
        is_overtime?: boolean;
      }
    | undefined
  >(undefined);
  const [isProcessing, setIsProcessing] = useState(false);
  const lastReadTimeRef = useRef<number>(0);
  const lastNotificationRef = useRef<Notification | null>(null);
  const COOLDOWN_MS = 3000; // 3秒間のクールダウン

  const handleCustomerCardDetected = useCallback(
    async (uid: string, customerId: number) => {
      // クールダウンチェック
      const now = Date.now();
      if (now - lastReadTimeRef.current < COOLDOWN_MS) {
        showNotification({
          title: '読み込み中',
          message: `少しお待ちください（${Math.ceil((COOLDOWN_MS - (now - lastReadTimeRef.current)) / 1000)}秒）`,
          color: 'orange',
          autoClose: 1000,
        });
        return;
      }

      if (isProcessing) {
        return;
      }

      lastReadTimeRef.current = now;

      setIsProcessing(true);

      try {
        // 入退室処理APIを呼び出し（V3版）
        const response = await apiClient.post('/attendance/customer', {
          customer_id: customerId,
          nfc_uid: uid,
          device_id: window.localStorage.getItem('device_id'),
        });

        // V3 APIレスポンスは {success: true, data: {...}} の形式
        const responseData = response.data.data || response.data;
        const { customer, attendance, action, message } = responseData;

        // 重複ブロック時はモーダル表示をスキップ（別タブで処理済み）
        if (action === 'duplicate_blocked' || action === 'already_checked_in') {
          return;
        }

        const isOvertime = attendance?.is_overtime || false;

        // モーダル表示用データセット（入退室情報を分離）
        if (customer) {
          const mappedData: CustomerInfo = {
            id: customer.id,
            name: customer.name,
            name_kana: customer.name_kana,
            birth_date: customer.birth_date,
            gender: customer.gender,
            photo_url: customer.photo_url,
            avatar: customer.avatar,
            memo: customer.memo,
            store: customer.store,
            status: customer.status,
          };

          setCustomerData(mappedData);
        }

        // 入退室情報を別で管理（時間外フラグを含む）
        if (attendance) {
          const mappedAttendance = {
            action: action as 'check_in' | 'check_out',
            check_in_time: attendance.check_in_time,
            check_out_time: attendance.check_out_time,
            stay_duration: attendance.stay_duration,
            is_overtime: isOvertime,
          };

          setAttendanceData(mappedAttendance);
        }

        // 通知モード判定
        const nfcMode = getNfcNotificationMode();
        const statusEmoji = isOvertime ? '🟡' : action === 'check_in' ? '🟢' : '🔵';
        const actionLabel = isOvertime ? '契約時間外' : action === 'check_in' ? '入室' : '退室';
        const customerName = customer?.name || '顧客';
        const storeName = customer?.store?.name || '';

        if (nfcMode === 'notification' && canSendBrowserNotification()) {
          // デスクトップ通知モード: モーダルを表示せずブラウザ通知
          const timeStr =
            action === 'check_in'
              ? attendance?.check_in_time || ''
              : attendance?.check_out_time || '';
          const durationStr =
            action === 'check_out' && attendance?.stay_duration
              ? ` | 滞在${attendance.stay_duration}`
              : '';
          const body = `${timeStr} ${actionLabel}${durationStr}${storeName ? ` | ${storeName}` : ''}`;
          const title = `${statusEmoji} ${actionLabel} - ${customerName}`;

          // 前の通知を閉じる
          if (lastNotificationRef.current) {
            lastNotificationRef.current.close();
          }

          const rawPhotoUrl =
            (customer?.photo_url && customer.photo_url.trim()) ||
            (customer?.avatar && customer.avatar.trim()) ||
            null;

          if (rawPhotoUrl) {
            // 写真あり: 画像をプリロードしてから通知表示
            const absoluteUrl = rawPhotoUrl.startsWith('http')
              ? rawPhotoUrl
              : `${window.location.origin}${rawPhotoUrl.startsWith('/') ? '' : '/'}${rawPhotoUrl}`;

            const img = new Image();
            img.onload = () => {
              lastNotificationRef.current = new Notification(title, {
                body,
                icon: absoluteUrl,
                image: absoluteUrl,
              });
            };
            img.onerror = () => {
              // 画像読込失敗時は頭文字アイコンにフォールバック
              lastNotificationRef.current = new Notification(title, {
                body,
                icon: generateInitialIcon(customerName, action as string),
              });
            };
            img.src = absoluteUrl;
          } else {
            // 写真なし: 頭文字アイコンで通知表示
            lastNotificationRef.current = new Notification(title, {
              body,
              icon: generateInitialIcon(customerName, action as string),
            });
          }
        } else {
          // モーダルモード: 従来通りモーダル表示
          setShowModal(true);
        }

        // Mantineトースト通知（どちらのモードでも表示）
        const notificationIcon = isOvertime ? (
          <IconClock />
        ) : action === 'check_in' ? (
          <IconLogin />
        ) : (
          <IconLogout />
        );
        const notificationColor = isOvertime ? 'yellow' : action === 'check_in' ? 'green' : 'blue';

        showNotification({
          title: `${actionLabel} - ${customerName}`,
          message: `${customerName}様の${message}`,
          color: notificationColor,
          icon: notificationIcon,
          autoClose: 3000,
        });
      } catch (error: any) {
        // エラーが404の場合（顧客が見つからない）
        if (error.response) {
          if (error.response?.status === 404) {
            showNotification({
              title: 'エラー',
              message: '顧客情報が見つかりません',
              color: 'red',
            });
          } else if (error.response?.status === 403) {
            showNotification({
              title: 'エラー',
              message: 'このカードは無効です',
              color: 'orange',
            });
          } else {
            showNotification({
              title: 'エラー',
              message: error.response?.data?.message || '読み取りエラーが発生しました',
              color: 'red',
            });
          }
        } else {
          showNotification({
            title: 'エラー',
            message: '読み取りエラーが発生しました',
            color: 'red',
          });
        }
      } finally {
        setIsProcessing(false);
      }
    },
    [isProcessing]
  );

  useEffect(() => {
    // NFC無効パスでは登録しない
    if (isDisabled) return;

    // NFCが接続されていない場合は待機
    if (!unifiedNFCService.isConnected()) {
      // NFC接続イベントを監視
      const unsubscribeConnection = unifiedNFCService.on('connected', () => {
        registerNFCListener();
      });

      return () => {
        unsubscribeConnection();
      };
    } else {
      return registerNFCListener();
    }
  }, [handleCustomerCardDetected, isDisabled]);

  const registerNFCListener = () => {
    // 統合NFCサービス経由でNFCカード検出を監視
    const unsubscribe = unifiedNFCService.on('nfc_detected', async (event) => {
      // 書き込み中は無視
      if (event.context === 'write') {
        return;
      }

      // NFCサービスが書き込みモード中なら無視
      if (unifiedNFCService.isWriteMode()) {
        return;
      }

      const { uid, type, entity_id } = event.data || {};

      const cardType = Number(type);

      // 顧客カード（type=2）の場合のみ処理
      if (cardType === 2) {
        if (uid && entity_id) {
          await handleCustomerCardDetected(uid, entity_id);
        }
      }
    });

    return () => {
      unsubscribe();
    };
  };

  // モーダルタイプの選択（localStorage または 環境設定から取得）
  // 'detailed' または 'default' を設定可能
  const modalType = localStorage.getItem('attendance_modal_type') || 'detailed';

  // 選択されたモーダルをレンダリング
  const ModalComponent =
    modalType === 'detailed' ? DetailedAttendanceModal : CustomerAttendanceModal;

  return (
    <ModalComponent
      opened={showModal}
      onClose={() => {
        setShowModal(false);
        setCustomerData(null);
        setAttendanceData(undefined);
      }}
      customer={customerData}
      attendance={attendanceData}
    />
  );
};

/**
 * 顧客名の頭文字からアイコン画像を動的生成（Canvas API）
 * 写真がない顧客のデスクトップ通知用フォールバック
 */
function generateInitialIcon(name: string, action: string): string {
  const canvas = document.createElement('canvas');
  canvas.width = 128;
  canvas.height = 128;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '/favicon.ico';

  // 背景色（入室=緑、退室=青、その他=グレー）
  const bgColor =
    action === 'check_in' ? '#4CAF50' : action === 'check_out' ? '#2196F3' : '#9E9E9E';
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.arc(64, 64, 64, 0, Math.PI * 2);
  ctx.fill();

  // 頭文字テキスト
  const initial = name.charAt(0) || '?';
  ctx.fillStyle = '#FFFFFF';
  ctx.font = 'bold 64px sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(initial, 64, 68);

  return canvas.toDataURL('image/png');
}
